/**
 * Catalogue des intégrations affichées dans la marketplace manager.
 *
 * Les libellés (name, category, description, longDescription) restent en
 * français/anglais via i18n (cf. manager.marketplace.integrations.{id}.*)
 * — ici on porte uniquement la structure et les données non-traduisibles
 * (logo, URL CTA, statut).
 */

import type { CalendarProvider } from '@/api/calendars'

export type IntegrationStatus = 'live' | 'coming-soon'

export interface IntegrationHowItWorksItem {
  /** Clé i18n du titre du bullet (ex: 'manager.marketplace.integrations.zapier.howItWorks.0.title') */
  titleKey: string
  /** Clé i18n du contenu du bullet */
  descriptionKey: string
}

export interface Integration {
  id: string
  /** Chemin vers le SVG dans public/integrations/{id}.svg (ou URL absolue). */
  logo: string
  /** Couleur d'accent (bg derrière le logo). Nom de classe Tailwind. */
  accentBg: string
  status: IntegrationStatus
  /** CTA primaire (ex: doc widget, app Zapier). Placeholder {locale} remplacé au runtime. */
  externalUrl?: string
  /** CTA secondaire optionnel (ex: Developer Console pour le widget). */
  secondaryUrl?: string
  /** Nombre d'items dans la liste "Comment ça marche" — utilisé pour itérer i18n */
  howItWorksCount: number
  /** Assistants IA : le bloc à copier pour brancher le serveur MCP. */
  branche?: BrancheMcp
  /** Se branche ailleurs dans le dashboard : le bouton de la page y mène. */
  ongletDashboard?: string
  /** Calendrier : fournisseur à ouvrir dans le dialogue de connexion. */
  calendrier?: CalendarProvider
}

export const ADRESSE_MCP = 'https://api.callizy.ca/mcp'

/** Comment coller l'adresse dans un assistant : un bloc, prêt à copier. */
export interface BrancheMcp {
  /** Nom du fichier où coller (barre du bloc). Vide : l'endroit vient d'i18n (`where`). */
  fichier: string
  /** Sans clé : la connexion Callizy s'ouvre au premier usage. */
  sansCle: string
  /** Avec une clé API, pour un serveur ou un compte partagé. */
  avecCle: string
}

const CLE = 'CLE:SECRET'

export const BRANCHEMENTS: Record<string, BrancheMcp> = {
  claude: {
    fichier: '',
    sansCle: ADRESSE_MCP,
    avecCle: ADRESSE_MCP,
  },
  chatgpt: {
    fichier: '',
    sansCle: ADRESSE_MCP,
    avecCle: ADRESSE_MCP,
  },
  'claude-code': {
    fichier: 'terminal',
    sansCle: `claude mcp add callizy --transport http ${ADRESSE_MCP}`,
    avecCle: `claude mcp add callizy \\\n  --transport http ${ADRESSE_MCP} \\\n  --header "Authorization: Bearer ${CLE}"`,
  },
  cursor: {
    fichier: '~/.cursor/mcp.json',
    sansCle: `{\n  "mcpServers": {\n    "callizy": { "url": "${ADRESSE_MCP}" }\n  }\n}`,
    avecCle: `{\n  "mcpServers": {\n    "callizy": {\n      "url": "${ADRESSE_MCP}",\n      "headers": { "Authorization": "Bearer ${CLE}" }\n    }\n  }\n}`,
  },
  vscode: {
    fichier: '.vscode/mcp.json',
    sansCle: `{\n  "servers": {\n    "callizy": { "type": "http", "url": "${ADRESSE_MCP}" }\n  }\n}`,
    avecCle: `{\n  "servers": {\n    "callizy": {\n      "type": "http",\n      "url": "${ADRESSE_MCP}",\n      "headers": { "Authorization": "Bearer ${CLE}" }\n    }\n  }\n}`,
  },
  codex: {
    fichier: '~/.codex/config.toml',
    sansCle: `[mcp_servers.callizy]\nurl = "${ADRESSE_MCP}"`,
    avecCle: `[mcp_servers.callizy]\nurl = "${ADRESSE_MCP}"\nhttp_headers = { Authorization = "Bearer ${CLE}" }`,
  },
  windsurf: {
    fichier: 'mcp_config.json',
    sansCle: `{\n  "mcpServers": {\n    "callizy": { "serverUrl": "${ADRESSE_MCP}" }\n  }\n}`,
    avecCle: `{\n  "mcpServers": {\n    "callizy": {\n      "serverUrl": "${ADRESSE_MCP}",\n      "headers": { "Authorization": "Bearer ${CLE}" }\n    }\n  }\n}`,
  },
}

const ASSISTANTS_MCP: Integration[] = [
  { id: 'claude', logo: '/integrations/assistants/claude.svg', accentBg: 'bg-[#d97757]' },
  { id: 'chatgpt', logo: '/integrations/assistants/openai.svg', accentBg: 'bg-[#10a37f]' },
  { id: 'claude-code', logo: '/integrations/assistants/claude.svg', accentBg: 'bg-[#1f1f1f]' },
  { id: 'cursor', logo: '/integrations/assistants/cursor.svg', accentBg: 'bg-[#1f1f1f]' },
  { id: 'vscode', logo: '/integrations/assistants/vscode.svg', accentBg: 'bg-white dark:bg-slate-100' },
  { id: 'codex', logo: '/integrations/assistants/openai.svg', accentBg: 'bg-[#1f1f1f]' },
  { id: 'windsurf', logo: '/integrations/assistants/windsurf.svg', accentBg: 'bg-[#0b3d3b]' },
].map((a) => ({
  ...a,
  status: 'live' as const,
  externalUrl: 'https://callizy.ca/{locale}/mcp',
  howItWorksCount: 2,
  branche: BRANCHEMENTS[a.id],
}))

export const INTEGRATIONS: Integration[] = [
  // Rendez-vous : se branchent dans Calendriers, la carte y mène.
  { id: 'google-calendar', logo: '/integrations/google-calendar.svg', accentBg: 'bg-white dark:bg-slate-100', status: 'live', howItWorksCount: 3, ongletDashboard: 'calendars', calendrier: 'google', externalUrl: 'https://callizy.ca/{locale}/integrations/google-calendar' },
  { id: 'microsoft-365', logo: '/integrations/microsoft-365.svg', accentBg: 'bg-white dark:bg-slate-100', status: 'live', howItWorksCount: 3, ongletDashboard: 'calendars', calendrier: 'microsoft', externalUrl: 'https://callizy.ca/{locale}/integrations/microsoft-365' },
  { id: 'square', logo: '/integrations/square.svg', accentBg: 'bg-white dark:bg-slate-100', status: 'live', howItWorksCount: 3, ongletDashboard: 'calendars', calendrier: 'square', externalUrl: 'https://callizy.ca/{locale}/integrations/square' },
  // No guide page on the site yet: no external link.
  { id: 'wix', logo: '/integrations/wix.svg', accentBg: 'bg-white dark:bg-slate-100', status: 'live', howItWorksCount: 3, ongletDashboard: 'calendars', calendrier: 'wix', externalUrl: 'https://callizy.ca/{locale}/integrations/wix' },
  {
    id: 'zapier',
    logo: '/integrations/zapier.svg',
    accentBg: 'bg-orange-100 dark:bg-orange-500/20',
    status: 'live',
    externalUrl: 'https://zapier.com/apps/callizy/integrations',
    howItWorksCount: 2,
  },
  {
    id: 'hubspot',
    logo: '/integrations/hubspot.svg',
    externalUrl: 'https://callizy.ca/{locale}/integrations/hubspot',
    accentBg: 'bg-[#ff7a59]/15 dark:bg-[#ff7a59]/20',
    status: 'live',
    // Le détail affiche le panneau de connexion (CrmPanel) ; le lien mène au guide.
    howItWorksCount: 3,
  },
  {
    id: 'pipedrive',
    logo: '/integrations/pipedrive.svg',
    externalUrl: 'https://callizy.ca/{locale}/integrations/pipedrive',
    accentBg: 'bg-[#017737]/15 dark:bg-[#017737]/25',
    status: 'live',
    howItWorksCount: 3,
  },
  {
    id: 'zoho',
    logo: '/integrations/zoho.svg',
    externalUrl: 'https://callizy.ca/{locale}/integrations/zoho',
    accentBg: 'bg-[#e42527]/15 dark:bg-[#e42527]/25',
    status: 'live',
    howItWorksCount: 3,
  },
  {
    id: 'google-contacts',
    logo: '/integrations/google-contacts.svg',
    externalUrl: 'https://callizy.ca/{locale}/integrations/google-contacts',
    accentBg: 'bg-[#4285f4]/15 dark:bg-[#4285f4]/25',
    status: 'live',
    howItWorksCount: 3,
  },
  {
    id: 'clio',
    logo: '/integrations/clio.svg',
    externalUrl: 'https://callizy.ca/{locale}/integrations/clio',
    accentBg: 'bg-[#0070e0]/15 dark:bg-[#0070e0]/25',
    status: 'live',
    howItWorksCount: 3,
  },
  {
    id: 'slack',
    logo: '/integrations/slack.svg',
    externalUrl: 'https://callizy.ca/{locale}/integrations/slack',
    accentBg: 'bg-[#4a154b]/15 dark:bg-[#e01e5a]/20',
    status: 'live',
    howItWorksCount: 3,
  },
  // Assistants IA : une carte par assistant, chacune avec son logo et le
  // bloc à coller. Tous parlent au même serveur MCP ; seule la façon de
  // coller l'adresse change.
  ...ASSISTANTS_MCP,
  {
    id: 'widget',
    // Favicon Callizy déjà hébergé via le CDN du widget SDK.
    logo: 'https://sdk.callizy.ca/assets/favicon_callizy.png',
    accentBg: 'bg-indigo-100 dark:bg-indigo-500/20',
    status: 'live',
    // Doc API du widget (Django sert /docs sans locale, gère sa propre i18n).
    externalUrl: 'https://callizy.ca/docs#description/widget-callizy-branding',
    // Portail Developer (bilingue) pour créer un client_id.
    secondaryUrl: 'https://callizy.ca/{locale}/developers',
    howItWorksCount: 2,
  },
  // HubSpot, Slack et Make retirés le 12 août 2026 : trois cartes grisées
  // « Bientôt » sur cinq donnaient l'impression d'un catalogue au tiers
  // vide, alors que les deux intégrations réelles sont solides. Mieux vaut
  // deux propositions qui fonctionnent que cinq dont trois sont des
  // promesses — surtout devant les entreprises du programme pilote.
  //
  // Le statut 'coming-soon' reste dans le type : le jour où l'une d'elles
  // arrive vraiment, on remet l'entrée sans retoucher au composant.
]

export function getIntegration(id: string | null | undefined): Integration | null {
  if (!id) return null
  return INTEGRATIONS.find((i) => i.id === id) || null
}
